import Diem from '@travelnest/diem';
import { IApiBooking, IApiEvent, IBooking, IEvent } from '@travelnest/typewriter';

import { ICancellationRequest } from '../data/entities/event';
import { IPostPayment } from '../data/entities/post-payment';
import { fromFloyd } from '../entities/pricing/helpers';
import { IConfiguredHttp } from './http';

export interface IMassBookingPaymentResult {
	paid: string[];
	ignored: string[];
}

export interface ITaxParameters {
	percentage: number;
	name: string;
}

export interface IHostCancellationPayment {
	booking_id: string;
	price: number;
	currency: string;
	description: string;
	tax?: ITaxParameters;
}

export interface IPaymentLinkData {
	payment_link: string;
	cancellation_date: string;
}

export interface IAvailabilityV3Api {
	getEventsByPropertyId: (id: string) => Promise<IEvent[]>;
	blockByDateRange: (propertyId: string, startOn: Diem, endOn: Diem) => Promise<IEvent[]>;
	fullyPayBookingsByIds: (eventIds: string[], onDate?: string) => Promise<IMassBookingPaymentResult>;
	maunallyConfirmBooking: (propertyId: string, eventId: string) => Promise<IEvent>;
	getBookingsBySearchText: (text: string) => Promise<IEvent[]>;
	createCancellationRequest: (propertyId: string, eventId: string, data: ICancellationRequest) => Promise<ICancellationRequest>;
	getHostCancellationPayment: (propertyId: string, eventId: string) => Promise<IHostCancellationPayment>;
	addHostCancellationPostPayment: (propertyId: string, eventId: string) => Promise<IPostPayment>;
	getPaymentLink: (propertyId: string, eventId: string) => Promise<IPaymentLinkData>;
	extendPaymentReminders: (propertyId: string, eventId: string, deferralHours: number) => Promise<void>;
	cancelPaymentReminders: (eventId: string) => Promise<void>;
}

const receiveBooking = (booking: IApiBooking): IBooking => ({
	...booking,
	value: {
		...booking.value,
		amount: fromFloyd(booking.value.amount)
	},
	payment_date: new Date(booking.payment_date)
});

const receiveEvent = (period: IApiEvent): IEvent => ({
	...period,
	booking: period.booking ? receiveBooking(period.booking) : (period.booking as any),
	start_on: new Date(period.start_on),
	end_on: new Date(period.end_on)
});

const availabilityV3ApiFactory = (http: IConfiguredHttp, prefix = ''): IAvailabilityV3Api => ({
	getEventsByPropertyId(id) {
		return http.get(`${prefix}/events/property/${id}`).then((data) => data.map(receiveEvent));
	},
	blockByDateRange(propertyId: string, startOn: Diem, endOn: Diem) {
		return http.post(
			`${prefix}/events/property/${propertyId}/start/${startOn}/end/${endOn}/managed_by/travelnest.com`
		).then((data) => data.map(receiveEvent));
	},
	fullyPayBookingsByIds(eventIds: string[], onDate?: string) {
		if (onDate) {
			return http.post(
				`${prefix}/bookings/receipts/owner/paid?on_date=${onDate}`, eventIds
			);
		}
		return http.post(
			`${prefix}/bookings/receipts/owner/paid`, eventIds
		);
	},
	maunallyConfirmBooking(propertyId: string, eventId: string) {
		return http.post(
			`${prefix}/bookings/property/${propertyId}/id/${eventId}/manual_confirmation`
		);
	},
	getBookingsBySearchText(text: string) {
		return http.get(`${prefix}/bookings/search?q=${text}`).then((data) => data.map(receiveEvent));
	},
	createCancellationRequest(propertyId: string, eventId: string, cancellationRequestData: ICancellationRequest) {
		return http.post(`${prefix}/cancellations/property/${propertyId}/id/${eventId}/request`, cancellationRequestData);
	},
	getHostCancellationPayment(propertyId: string, eventId: string) {
		return http.get(`${prefix}/bookings/property/${propertyId}/id/${eventId}/host_cancellation_payment`);
	},
	addHostCancellationPostPayment(propertyId: string, eventId: string) {
		return http.post(`${prefix}/bookings/property/${propertyId}/id/${eventId}/post_payment/host_cancellation`);
	},
	cancelPaymentReminders(eventId: string) {
		return http.delete(`${prefix}/payment_reminders/event/${eventId}`);
	},
	getPaymentLink(propertyId: string, eventId: string) {
		return http.get(`${prefix}/bookings/property/${propertyId}/id/${eventId}/payment_link`);
	},
	extendPaymentReminders(propertyId: string, eventId: string, deferralHours: number) {
		return http.put(`${prefix}/bookings/property/${propertyId}/id/${eventId}/reminder`, { hours_deferred: deferralHours });
	},
});

export default availabilityV3ApiFactory;
